import { supabase } from "./supabase";

export async function checkAlreadySubmitted(clientId) {
  if (!clientId) return false;
  const { data, error } = await supabase
    .from("intake_submissions")
    .select("id, status")
    .eq("client_id", clientId)
    .in("status", ["pending", "processing", "complete"])
    .maybeSingle();
  if (error) return false;
  return !!data;
}

export async function submitIntake(formData, clientId) {
  const tradePhotos = (formData.tradePhotos || []).map(p => ({ name: p.name, type: p.type }));
  const d = { ...formData, tradePhotos };

  const lines = [
    `CLIENT INTAKE SUBMISSION`,
    `========================`,
    clientId ? `Client ID: ${clientId}` : `Client ID: (none)`,
    ``,
    `CONTACT`,
    `-------`,
    `Name: ${d.firstName} ${d.lastName}`,
    `Email: ${d.intakeEmail || "—"}`,
    `Phone: ${d.intakePhone || "—"}`,
    ``,
    `REGISTRATION`,
    `------------`,
    `City: ${d.registrationCity || "—"}`,
    `State: ${d.registrationState || "—"}`,
    `Zip: ${d.registrationZip || "—"}`,
    ``,
    `VEHICLE`,
    `-------`,
    `Condition: ${d.vehicleCondition}`,
    `Year: ${d.vehicleYear}`,
    `Make: ${d.vehicleMake}`,
    `Model: ${d.vehicleModel}`,
    `Trim: ${d.vehicleTrim}`,
    d.vehiclePackages ? `Packages: ${d.vehiclePackages}` : null,
    `Exterior Color: ${d.vehicleExteriorColor || "—"}`,
    `Interior Color: ${d.vehicleInteriorColor || "—"}`,
    d.vehicleCondition === "new" ? `Purchase or Lease: ${d.purchaseOrLease}` : `Purchase or Lease: Purchase (used)`,
    ``,
    `VEHICLE SEARCH`,
    `--------------`,
    `Has Specific Vehicle: ${d.hasSpecificVehicle}`,
    d.hasSpecificVehicle === "yes" ? [
      d.specificDealer ? `Dealer: ${d.specificDealer}, ${d.specificDealerCity} ${d.specificDealerState}` : null,
      d.specificStock ? `Stock #: ${d.specificStock}` : null,
      d.specificAsking ? `Asking Price: $${d.specificAsking}` : null,
      `Reached Out: ${d.hasReachedOut || "—"}`,
      d.reachedOutResponse ? `Dealer Response: ${d.reachedOutResponse}` : null,
    ].filter(Boolean).join("\n") : null,
    d.hasSpecificVehicle === "no" && d.vehicleCondition === "used" ? [
      d.maxMileage ? `Max Mileage: ${d.maxMileage}` : null,
      d.cpoCertified ? `CPO: ${d.cpoCertified}` : null,
      d.usedBudget ? `Max Budget OTD: $${d.usedBudget}` : null,
    ].filter(Boolean).join("\n") : null,
    ``,
    `PAYMENT`,
    `-------`,
    d.purchaseOrLease === "lease" ? [
      `Type: Lease`,
      d.leaseMilesPerYear ? `Miles/Year: ${d.leaseMilesPerYear}` : null,
      d.leaseTerm ? `Lease Term: ${d.leaseTerm} months` : null,
      d.targetMonthlyPayment ? `Target Monthly: $${d.targetMonthlyPayment}` : null,
    ].filter(Boolean).join("\n") : [
      `Method: ${d.paymentMethod}`,
      d.loanTerm ? `Loan Term: ${d.loanTerm} months` : null,
      d.creditScore ? `Credit Score: ${d.creditScore}` : null,
      d.outsideLender ? `Lender: ${d.outsideLender}` : null,
      d.outsideRate ? `Rate: ${d.outsideRate}` : null,
      d.targetMonthlyPayment ? `Target Monthly: $${d.targetMonthlyPayment}` : null,
    ].filter(Boolean).join("\n"),
    ``,
    `TRADE-IN`,
    `--------`,
    `Has Trade: ${d.hasTrade}`,
    d.hasTrade === "yes" ? [
      `Vehicle: ${d.tradeYear} ${d.tradeMake} ${d.tradeModel} ${d.tradeTrim}`,
      d.tradeVin ? `VIN: ${d.tradeVin}` : null,
      `Mileage: ${d.tradeMileage}`,
      `Condition: ${d.tradeCondition}`,
      `Accident History: ${d.tradeAccident}`,
      d.tradeAccidentDesc ? `Accident Desc: ${d.tradeAccidentDesc}` : null,
      `Outstanding Loan: ${d.tradeLoan}`,
      d.tradeLoanPayoff ? `Payoff: $${d.tradeLoanPayoff}` : null,
      d.tradeLoanLender ? `Lender: ${d.tradeLoanLender}` : null,
      `Existing Offer: ${d.tradeExistingOffer}`,
      d.tradeOfferSource ? `Offer From: ${d.tradeOfferSource}` : null,
      d.tradeOfferAmount ? `Offer Amount: $${d.tradeOfferAmount}` : null,
      d.tradeClientEstimate ? `Client Estimate: $${d.tradeClientEstimate}` : null,
      d.tradePhotos.length > 0 ? `Photos: ${d.tradePhotos.map(p => p.name).join(", ")}` : null,
    ].filter(Boolean).join("\n") : null,
    ``,
    `TRAVEL & TIMELINE`,
    `-----------------`,
    `Travel Distance: ${d.travelDistance}`,
    `Timeline: ${d.timeline}`,
    ``,
    `ADD-ONS & COVERAGE`,
    `------------------`,
    `GAP Interest: ${d.gapInterest}`,
    `Warranty Interest: ${d.warrantyInterest}`,
    `Considering Add-Ons: ${d.hasAddOns}`,
    d.hasAddOns === "yes" ? `Add-Ons: ${d.addOnsDescription}` : null,
    ``,
    `EXPECTATIONS`,
    `------------`,
    d.maxBudget ? `Max Budget OTD: $${d.maxBudget}` : null,
    `Success Outcome: ${d.successOutcome || "—"}`,
    ``,
    `SCHEDULING`,
    `----------`,
    `Best Time for Call: ${d.callBestTime}`,
    `Weekend Availability: ${d.callWeekend || "—"}`,
    `Preferred Contact: ${d.contactMethod}`,
    ``,
    `CONCERNS & QUESTIONS`,
    `--------------------`,
    d.anythingElse || "(none)",
  ].filter(line => line !== null).join("\n");

  const res = await fetch("https://formspree.io/f/xjgaongz", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({
      _subject: `Even Ground Intake — ${d.firstName} ${d.lastName} — ${d.vehicleYear} ${d.vehicleMake} ${d.vehicleModel}`,
      message: lines,
      email: d.intakeEmail || "no-email@getevenground.com",
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Formspree error ${res.status}`);
  }
}
